import React, { useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CalendarDays,
  Mail,
  Phone,
} from "lucide-react";

const STATUS_PRESENTATION = {
  ACCEPTED: {
    label: "Accepted",
    className: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  },
  PENDING: {
    label: "Pending",
    className: "bg-blue-50 text-blue-700 ring-blue-200",
  },
  REGISTERED: {
    label: "Pending",
    className: "bg-blue-50 text-blue-700 ring-blue-200",
  },
  REJECTED_DUPLICATE: {
    label: "Duplicate",
    className: "bg-amber-50 text-amber-800 ring-amber-200",
  },
  REJECTED_INVALID: {
    label: "Not accepted",
    className: "bg-red-50 text-red-700 ring-red-200",
  },
  CONVERTED: {
    label: "Converted",
    className: "bg-teal-50 text-teal-700 ring-teal-200",
  },
  SUBSCRIBER: {
    label: "Converted",
    className: "bg-teal-50 text-teal-700 ring-teal-200",
  },
  WON: {
    label: "Converted",
    className: "bg-teal-50 text-teal-700 ring-teal-200",
  },
  CLOSED_WON: {
    label: "Converted",
    className: "bg-teal-50 text-teal-700 ring-teal-200",
  },
  ACTIVE_SUBSCRIBER: {
    label: "Converted",
    className: "bg-teal-50 text-teal-700 ring-teal-200",
  },
  WITHDRAWN: {
    label: "Withdrawn",
    className: "bg-red-50 text-red-700 ring-red-200",
  },
};

function normalizeStatus(value) {
  return String(value || "REGISTERED")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

function StatusPill({ status }) {
  const normalized = normalizeStatus(status);
  const presentation =
    STATUS_PRESENTATION[normalized] || {
      label: normalized
        .toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      className: "bg-slate-100 text-slate-600 ring-slate-200",
    };

  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] ring-1 ${presentation.className}`}
    >
      {presentation.label}
    </span>
  );
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function getRecommendedAction(status) {
  const normalized = normalizeStatus(status);

  if (normalized === "ACCEPTED") return "Contact the decision-maker and confirm the next step.";
  if (normalized === "REJECTED_DUPLICATE") return "No action required; this business is already protected.";
  if (normalized === "WITHDRAWN") return "This prospect is no longer protected and has left your active portfolio.";
  if (["CONVERTED", "SUBSCRIBER", "WON", "CLOSED_WON", "ACTIVE_SUBSCRIBER"].includes(normalized)) {
    return "This prospect has converted. Review the attributed Customer record and commission activity in the Commercial workspace.";
  }
  if (["CONTACTED", "ENGAGED", "QUALIFIED", "IN_PROGRESS"].includes(normalized)) {
    return "Maintain momentum and document the next commercial step.";
  }
  return "Monitor the review status and prepare your outreach plan.";
}

export default function SalesAffiliateLeadRow({ lead, onWithdraw }) {
  const [confirming, setConfirming] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [rowError, setRowError] = useState("");

  const normalizedStatus = normalizeStatus(lead.status);
  const canWithdraw = normalizedStatus === "ACCEPTED";

  const handleConfirmWithdraw = async () => {
    if (withdrawing) return;

    setWithdrawing(true);
    setRowError("");

    try {
      await onWithdraw(lead.id);
      setConfirming(false);
    } catch (err) {
      setRowError(err?.message || "Unable to withdraw this lead.");
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:border-blue-200 hover:shadow-md">
      <div className="border-b border-slate-100 bg-gradient-to-r from-blue-50/70 via-white to-teal-50/50 px-5 py-5 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-600/10 text-blue-700 ring-1 ring-blue-600/15">
              <Building2 size={20} />
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-lg font-black tracking-tight text-slate-950">
                {lead.company_name || "Unnamed company"}
              </h3>
              <p className="mt-1 text-sm font-semibold text-slate-600">
                {[lead.first_name, lead.last_name].filter(Boolean).join(" ") ||
                  "Contact unavailable"}
              </p>
            </div>
          </div>
          <StatusPill status={lead.status} />
        </div>
      </div>

      <div className="grid gap-5 px-5 py-5 sm:grid-cols-[1fr_1fr_1.15fr] sm:px-6">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
            Contact
          </div>
          <div className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-blue-600" />
              <span className="truncate">{lead.email || "—"}</span>
            </div>
            {lead.phone ? (
              <div className="flex items-center gap-2">
                <Phone size={14} className="text-teal-600" />
                <span>{lead.phone}</span>
              </div>
            ) : null}
          </div>
        </div>

        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
            Prospect activity
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm font-black text-slate-900">
            <CalendarDays size={16} className="text-cyan-600" />
            {normalizedStatus === "WITHDRAWN"
              ? `Withdrawn ${formatDate(lead.withdrawn_at)}`
              : ["CONVERTED", "SUBSCRIBER", "WON", "CLOSED_WON", "ACTIVE_SUBSCRIBER"].includes(normalizedStatus)
                ? `Converted ${formatDate(lead.converted_at)}`
                : `Registered ${formatDate(lead.created_at)}`}
          </div>
        </div>

        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700">
            Recommended next step
          </div>
          <p className="mt-2 text-sm font-semibold leading-6 text-slate-700">
            {getRecommendedAction(lead.status)}
          </p>
        </div>
      </div>

      {canWithdraw ? (
        <div className="border-t border-slate-100 px-5 py-4 sm:px-6">
          {!confirming ? (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-2 text-xs font-black text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Withdraw protection
              <ArrowRight size={14} />
            </button>
          ) : (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 flex-none text-amber-700" size={18} />
                <p className="text-sm font-semibold text-amber-800">
                  Withdrawal removes protection from this prospect and may allow another Sales Affiliate to register it. This action
                  cannot be undone from this screen.
                </p>
              </div>

              {rowError ? (
                <div
                  role="alert"
                  className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700"
                >
                  {rowError}
                </div>
              ) : null}

              <div className="mt-3 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleConfirmWithdraw}
                  disabled={withdrawing}
                  className="rounded-2xl bg-emerald-600 px-4 py-2 text-xs font-black text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {withdrawing ? "Withdrawing..." : "Confirm withdrawal"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirming(false);
                    setRowError("");
                  }}
                  disabled={withdrawing}
                  className="rounded-2xl border border-slate-300 bg-white px-4 py-2 text-xs font-black text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </article>
  );
}
